import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { QUOTES_KEY } from './types';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

type SupplierCost = { id: string; supplierId: string; supplierName: string; name: string; costGrosze: number };
type Supplier = { id: string; name: string };
const costsKey = (versionId: string) => [...QUOTES_KEY, 'supplier-costs', versionId];

/**
 * "Sam koszt u dostawcy" (owner 2026-10-21 00:32): a cost at a chosen supplier with no line for the client — in the
 * profitability only, never in the quote's lines or the PDF. The list under "Koszt / Wynik / Marża".
 */
export function SupplierCostsList({ versionId, editable }: { versionId: string; editable: boolean }) {
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: costsKey(versionId), queryFn: () => api<{ costs: SupplierCost[] }>(`/api/quotes/versions/${versionId}/supplier-costs`) });
  const [error, setError] = useState('');
  const list = data.data?.costs ?? [];
  if (list.length === 0) return null;
  // no "are you sure" in the quote (owner): × removes it at once
  async function remove(c: SupplierCost) {
    try {
      await api(`/api/quotes/supplier-costs/${c.id}`, { method: 'DELETE' });
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się usunąć');
    }
    await queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
  }
  return (
    <div className="q-supplier-costs" data-feature-id="quotes.supplier_cost">
      <p className="q-supplier-costs-title">Koszty u dostawców bez pozycji (klient ich nie widzi):</p>
      <ul>
        {list.map((c) => (
          <li key={c.id}>
            <span>
              {c.supplierName}: {c.name}
            </span>
            <span className="num">{formatPln(c.costGrosze)}</span>
            {editable && (
              <button className="btn icon ghost" type="button" aria-label={`Usuń koszt: ${c.name}`} title="Usuń koszt" onClick={() => void remove(c)}>
                ×
              </button>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function SupplierCostDialog({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const suppliers = useQuery({ queryKey: ['contractors', 'suppliers'], queryFn: () => api<{ contractors: Supplier[] }>('/api/contractors?kind=supplier') });
  const [supplierId, setSupplierId] = useState('');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Adds the cost; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    if (!supplierId) return (setError('Wybierz dostawcę'), false);
    if (name.trim() === '') return (setError('Wpisz, za co jest koszt'), false);
    let costGrosze: number;
    try {
      costGrosze = parsePln(amount);
    } catch (err) {
      return (setError((err as Error).message), false);
    }
    setBusy(true);
    try {
      await api(`/api/quotes/versions/${versionId}/supplier-costs`, { method: 'POST', body: { supplierId, name: name.trim(), costGrosze } });
      await queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      onClose();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? (fieldIssues(err)[0]?.message ?? err.message) : 'Nie udało się dodać kosztu');
      return false;
    } finally {
      setBusy(false);
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({ dirty: supplierId !== '' || name.trim() !== '' || amount.trim() !== '', save: () => persist() });
  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="q-supplier-cost-title" onSubmit={submit} data-feature-id="quotes.supplier_cost">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="q-supplier-cost-title">Sam koszt u dostawcy</h2>
        <p className="hint">Koszt bez pozycji dla klienta, w tej wersji: liczy się do rentowności i rozliczeń z dostawcą (gdy ta wersja obowiązuje), nie trafia do wyceny ani PDF.</p>
        <label className="field">
          <span>Dostawca</span>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Wybierz dostawcę</option>
            {(suppliers.data?.contractors ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Za co</span>
          <input value={name} maxLength={300} onChange={(e) => setName(e.target.value)} placeholder="np. Transport od dostawcy" />
        </label>
        <label className="field narrow-field">
          <span>Kwota netto (zł)</span>
          <NumberInput kind="money" value={amount} onValueChange={setAmount} />
        </label>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={busy}>
            Dodaj koszt
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
